// src/features/insights/components/CryptoInsightTable.tsx
//
// Digital-asset sibling of <InsightTable>. Renders one side of the crypto
// swing-trading leaderboard (accumulation vs. breakdown) based on the `theme`
// prop. Themed ORANGE for buys / RED for sells to visually distinguish digital
// assets from traditional equities.
//
// Clicking a row teleports (client-side) to the crypto deep-dive page. Every
// className is a full static string so Tailwind's JIT purge keeps them.

import { useNavigate } from 'react-router-dom';

import type { CryptoInsightItem } from '../api';

interface CryptoInsightTableProps {
    title: string;
    items: CryptoInsightItem[];
    theme: 'buy' | 'sell';
}

export const CryptoInsightTable = ({ items, theme }: CryptoInsightTableProps) => {
    const navigate = useNavigate();
    const isBuy = theme === 'buy';

    const bgHeader = isBuy
        ? 'bg-orange-50 dark:bg-orange-900/20'
        : 'bg-red-50 dark:bg-red-900/20';
    const textColor = isBuy
        ? 'text-orange-700 dark:text-orange-400'
        : 'text-red-700 dark:text-red-400';
    const borderColor = isBuy
        ? 'border-orange-200 dark:border-orange-800'
        : 'border-red-200 dark:border-red-800';
    const hoverBorder = isBuy
        ? 'hover:border-l-orange-500'
        : 'hover:border-l-red-500';

    const formatCurrency = (val?: number | null) =>
        val != null
            ? new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: val < 1 ? 4 : 2,
              }).format(val)
            : '—';

    return (
        <div
            className={`flex flex-col h-full rounded-xl border ${borderColor} bg-white dark:bg-slate-900 shadow-sm overflow-hidden`}
        >
            <div
                className={`${bgHeader} px-5 py-4 border-b ${borderColor} flex items-center justify-between`}
            >
                <h2
                    className={`text-lg font-black tracking-tight ${textColor} flex items-center gap-2`}
                >
                    {isBuy ? 'CRYPTO ACCUMULATION' : 'CRYPTO BREAKDOWNS'}
                </h2>
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest bg-white dark:bg-slate-800 px-2 py-1 rounded shadow-sm">
                    Top 5 Ranked
                </span>
            </div>

            <div className="flex-1">
                {items.length === 0 ? (
                    <div className="p-8 text-center text-slate-500 h-full flex items-center justify-center">
                        No actionable {theme.toUpperCase()} signals today.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 dark:bg-slate-800/50 text-[10px] uppercase font-bold text-slate-500 border-b border-slate-200 dark:border-slate-700">
                                <tr>
                                    <th className="px-4 py-3 text-center w-8">#</th>
                                    <th className="px-4 py-3">Asset</th>
                                    <th className="px-4 py-3 text-right">Close</th>
                                    <th className="px-4 py-3 text-center">RSI(14)</th>
                                    <th className="px-4 py-3 text-right">
                                        {isBuy ? 'Exp. Return' : 'Signal'}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {items.map((asset, idx) => (
                                    <tr
                                        key={asset.symbol}
                                        onClick={() =>
                                            navigate(
                                                `/dashboard/crypto-info-page?symbol=${encodeURIComponent(
                                                    asset.symbol,
                                                )}`,
                                            )
                                        }
                                        className={`group cursor-pointer transition-colors border-l-4 border-transparent ${hoverBorder} hover:bg-slate-50 dark:hover:bg-slate-800/70`}
                                        title={asset.reasoning}
                                    >
                                        <td className="px-4 py-3 text-xs font-bold text-slate-400 text-center w-8">
                                            {idx + 1}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="font-bold text-blue-600 dark:text-blue-400 group-hover:underline">
                                                {asset.symbol}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono font-medium text-slate-900 dark:text-white">
                                            {formatCurrency(asset.close_price)}
                                        </td>
                                        <td className="px-4 py-3 text-center font-mono text-slate-500">
                                            {asset.rsi_14?.toFixed(1) || '—'}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {isBuy ? (
                                                <span
                                                    className={`font-mono font-bold px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 border ${borderColor} ${textColor}`}
                                                >
                                                    {asset.expected_return_pct
                                                        ? `+${asset.expected_return_pct.toFixed(
                                                              2,
                                                          )}%`
                                                        : '—'}
                                                </span>
                                            ) : (
                                                <span
                                                    className={`font-bold px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 border ${borderColor} ${textColor} text-[10px]`}
                                                >
                                                    {asset.signal}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CryptoInsightTable;
